import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section, SectionHeader } from '../ui/Section';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiUsers, FiCalendar, FiClock, FiCheck, FiInfo, FiAlertCircle, FiHome, FiSun, FiDollarSign } from 'react-icons/fi';

export const QuickEstimate = () => {
  // --- Constants & Pricing Data ---
  const EVENT_MINIMUMS = {
    weekday: 16000,
    friday: 23000,
    saturday: 31000,
    sunday: 20000
  };

  const VENUE_RENTAL_RATES = {
    weekday: 3000,
    friday: 5000,
    saturday: 6500,
    sunday: 4000
  };

  const DINING_RATES = {
    buffet: 75,
    sitDown: 82,
    themed: 78 // Updated from 76 to 78 as per request
  };

  const BAR_RATES = {
    none: [0, 0, 0, 0, 0, 0],
    bws: [24, 28, 32, 36, 40, 44],
    superior: [29, 33, 37, 41, 45, 49],
    deluxe: [33, 37, 41, 45, 49, 53]
  };

  const PARKING_FEE = 500;
  const BAR_TAX_RATE = 0.0975;

  // --- State ---
  const [guestCount, setGuestCount] = useState(0); 
  const [day, setDay] = useState('saturday');
  const [eventType, setEventType] = useState('ceremonyReception');
  const [diningStyle, setDiningStyle] = useState('buffet');
  const [barMode, setBarMode] = useState('package'); // 'package' or 'monetary'
  const [barPackage, setBarPackage] = useState('bws');
  const [barDuration, setBarDuration] = useState(5);
  const [monetaryBarAmount, setMonetaryBarAmount] = useState(3500);
  const [diningLocation, setDiningLocation] = useState('indoor');
  const [customVenueRental, setCustomVenueRental] = useState(null);
  const [enhancements, setEnhancements] = useState({
    barware: false,
    chargers: false,
    beadChargers: false,
    chairs: false,
    rings: false,
    lounge: false
  });

  // --- Logic ---
  const totals = useMemo(() => {
    // 1. Calculate Eligible Service Subtotal (Food, Ceremony, Enhancements)
    const foodCost = guestCount * DINING_RATES[diningStyle];
    const ceremonyFee = eventType === 'ceremonyReception' ? 2000 : 0;

    let enhancementCost = 0;
    if (enhancements.barware) enhancementCost += guestCount * 3;
    if (enhancements.chargers) enhancementCost += guestCount * 2;
    if (enhancements.beadChargers) enhancementCost += guestCount * 7;
    if (enhancements.chairs) enhancementCost += guestCount * 10;
    if (enhancements.rings) enhancementCost += guestCount * 2;
    if (enhancements.lounge) enhancementCost += 800;

    const eligibleServiceSubtotal = foodCost + ceremonyFee + enhancementCost;
    
    // 2. Event Minimum Check (Based on Eligible Service Items Only)
    const minRequirement = EVENT_MINIMUMS[day];
    const isBelowMinimum = eligibleServiceSubtotal < minRequirement;
    
    // The admin fee applies to the higher of the actual spend or the minimum
    const adminFeeBase = isBelowMinimum ? minRequirement : eligibleServiceSubtotal;

    // 3. Admin Fee Calculation
    const adminRate = diningLocation === 'outdoor' ? 0.25 : 0.22;
    const adminFee = adminFeeBase * adminRate;

    // 4. Venue Rental
    const venueRental = customVenueRental !== null ? customVenueRental : VENUE_RENTAL_RATES[day];

    // 5. Bar Calculation (With Alcohol Tax)
    let barEstimate = 0;
    if (barMode === 'package') {
      const barRate = BAR_RATES[barPackage][barDuration - 1] || 0;
      barEstimate = guestCount * barRate;
    } else {
      barEstimate = monetaryBarAmount;
    }
    const barTax = barEstimate * BAR_TAX_RATE;

    // 6. Final Total
    const totalIncludingAll = adminFeeBase + adminFee + venueRental + barEstimate + barTax + PARKING_FEE;

    return {
      foodCost,
      ceremonyFee,
      enhancementCost,
      eligibleServiceSubtotal,
      minRequirement,
      isBelowMinimum,
      adminFeeBase,
      adminRate,
      adminFee,
      venueRental,
      barEstimate,
      barTax,
      totalIncludingAll
    };
  }, [guestCount, day, eventType, diningStyle, barMode, barPackage, barDuration, monetaryBarAmount, enhancements, diningLocation, customVenueRental]);

  const toggleEnhancement = (key) => {
    setEnhancements(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Section id="quick-estimate" bg="bg-vintage-white">
      <SectionHeader 
        title="Quick Estimate Preview" 
        subtitle="Explore a planning range based on your guest count, service style, bar package, and selected enhancements."
      />

      <div className="max-w-6xl mx-auto">
        {/* Intro Disclaimer */}
        <div className="mb-10 p-6 bg-[#F6F1E8] rounded-xl border border-[#D9CCBA] flex gap-4 items-start">
          <SafeIcon icon={FiInfo} className="text-[#A67C52] w-6 h-6 flex-shrink-0" />
          <p className="text-[13px] md:text-[14px] text-[#5A4638] leading-[1.6] italic">
            This preview is designed to help couples explore possible planning ranges. It is not a final quote, proposal, contract, or guarantee of availability. Final pricing is prepared by the Padua Weddings team based on date, guest count, menu selections, bar service, rentals, administrative fee, tax, and event details.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Inputs Column */}
          <div className="lg:col-span-3 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Guest Count */}
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-[#2F2B28] tracking-wide uppercase flex items-center gap-2">
                  <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" /> Guest Count
                </label>
                <input 
                  type="number" 
                  placeholder="Enter guest count"
                  value={guestCount === 0 ? '' : guestCount}
                  onChange={(e) => setGuestCount(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full bg-white border border-[#D9CCBA] px-4 py-3.5 rounded-xl text-[#2F2B28] font-medium focus:ring-2 focus:ring-[#A67C52]/20 outline-none transition-all placeholder:text-[#7A6A5F]/40"
                />
              </div>

              {/* Event Day */}
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-[#2F2B28] tracking-wide uppercase flex items-center gap-2">
                  <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" /> Event Day
                </label>
                <select 
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  className="w-full bg-white border border-[#D9CCBA] px-4 py-3.5 rounded-xl text-[#2F2B28] font-medium focus:ring-2 focus:ring-[#A67C52]/20 outline-none appearance-none cursor-pointer"
                >
                  <option value="weekday">Weekday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday / Holiday</option>
                  <option value="sunday">Sunday</option>
                </select>
              </div>

              {/* Event Type */}
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-[#2F2B28] tracking-wide uppercase">Event Type</label>
                <select 
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-full bg-white border border-[#D9CCBA] px-4 py-3.5 rounded-xl text-[#2F2B28] font-medium focus:ring-2 focus:ring-[#A67C52]/20 outline-none appearance-none cursor-pointer"
                >
                  <option value="receptionOnly">Reception Only</option>
                  <option value="ceremonyReception">Ceremony + Reception</option>
                  <option value="ceremonyOnly">Ceremony Only</option>
                </select>
              </div>

              {/* Dining Style */}
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-[#2F2B28] tracking-wide uppercase">Dining Style</label>
                <select 
                  value={diningStyle}
                  disabled={eventType === 'ceremonyOnly'}
                  onChange={(e) => setDiningStyle(e.target.value)}
                  className="w-full bg-white border border-[#D9CCBA] px-4 py-3.5 rounded-xl text-[#2F2B28] font-medium focus:ring-2 focus:ring-[#A67C52]/20 outline-none appearance-none cursor-pointer disabled:bg-[#F6F1E8] disabled:text-[#7A6A5F]"
                >
                  <option value="buffet">Buffet Entrée</option>
                  <option value="sitDown">Sit-Down Entrée</option>
                  <option value="themed">Themed Buffet</option>
                </select>
              </div>
            </div>

            {/* Hosted Bar Selection */}
            <div className="space-y-4">
              <label className="text-[12px] font-bold text-[#2F2B28] tracking-wide uppercase">Hosted Bar Selection</label>
              <div className="flex gap-2 p-1 bg-[#F6F1E8] rounded-xl border border-[#D9CCBA]">
                <button
                  onClick={() => setBarMode('package')}
                  className={`flex-1 py-3 rounded-lg text-[13px] font-bold transition-all ${barMode === 'package' ? 'bg-white text-[#2F2B28] shadow-sm' : 'text-[#7A6A5F] hover:text-[#2F2B28]'}`}
                >
                  Hosted Bar Package
                </button>
                <button
                  onClick={() => setBarMode('monetary')}
                  className={`flex-1 py-3 rounded-lg text-[13px] font-bold transition-all ${barMode === 'monetary' ? 'bg-white text-[#2F2B28] shadow-sm' : 'text-[#7A6A5F] hover:text-[#2F2B28]'}`}
                >
                  Monetary Hosted Amount
                </button>
              </div>

              {barMode === 'package' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in duration-300">
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-[#7A6A5F] uppercase tracking-wider">Package Tier</label>
                    <select 
                      value={barPackage}
                      disabled={eventType === 'ceremonyOnly'}
                      onChange={(e) => setBarPackage(e.target.value)}
                      className="w-full bg-white border border-[#D9CCBA] px-4 py-3 rounded-xl text-[#2F2B28] font-medium outline-none"
                    >
                      <option value="none">No hosted bar package</option>
                      <option value="bws">Beer, Wine & Soda</option>
                      <option value="superior">Superior Brands</option>
                      <option value="deluxe">Deluxe Brands</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-[#7A6A5F] uppercase tracking-wider">Duration</label>
                    <select 
                      value={barDuration}
                      disabled={eventType === 'ceremonyOnly' || barPackage === 'none'}
                      onChange={(e) => setBarDuration(parseInt(e.target.value))}
                      className="w-full bg-white border border-[#D9CCBA] px-4 py-3 rounded-xl text-[#2F2B28] font-medium outline-none"
                    >
                      {[1,2,3,4,5,6].map(h => <option key={h} value={h}>{h} Hour{h > 1 ? 's' : ''}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 animate-in fade-in duration-300">
                  <label className="text-[11px] font-bold text-[#7A6A5F] uppercase tracking-wider">Hosted Bar Amount ($)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A6A5F] font-bold">$</span>
                    <input 
                      type="number"
                      value={monetaryBarAmount}
                      onChange={(e) => setMonetaryBarAmount(Number(e.target.value))}
                      className="w-full bg-white border border-[#D9CCBA] pl-8 pr-4 py-3 rounded-xl text-[#2F2B28] font-bold outline-none"
                    />
                  </div>
                  <p className="text-[11px] text-[#7A6A5F] italic">Couples may choose to host a starting monetary amount for the bar, such as $3,500, and add more if needed.</p>
                </div>
              )}
            </div>

            {/* Venue Rental Input */}
            <div className="space-y-2">
              <label className="text-[12px] font-bold text-[#2F2B28] tracking-wide uppercase flex items-center gap-2">
                <SafeIcon icon={FiDollarSign} className="w-3.5 h-3.5" /> Venue Rental (If Applicable)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A6A5F] font-bold">$</span>
                <input 
                  type="number" 
                  value={customVenueRental !== null ? customVenueRental : VENUE_RENTAL_RATES[day]}
                  onChange={(e) => setCustomVenueRental(parseInt(e.target.value) || 0)}
                  className="w-full bg-white border border-[#D9CCBA] pl-8 pr-4 py-3.5 rounded-xl text-[#2F2B28] font-medium focus:ring-2 focus:ring-[#A67C52]/20 outline-none"
                />
              </div>
              <p className="text-[11px] text-[#7A6A5F] italic">Standard rental for this day is ${VENUE_RENTAL_RATES[day].toLocaleString()}. Adjust if you have a custom quote.</p>
            </div>

            {/* Dining Experience Selection */}
            <div className="space-y-3">
              <label className="text-[12px] font-bold text-[#2F2B28] tracking-wide uppercase">Dining Experience</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setDiningLocation('indoor')}
                  className={`flex-1 p-5 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                    diningLocation === 'indoor'
                      ? 'bg-[#F6F1E8] border-[#6E7451] text-[#2F2B28]'
                      : 'bg-white border-[#D9CCBA] text-[#5A4638] hover:border-[#A67C52]/50'
                  }`}
                >
                  <SafeIcon icon={FiHome} className="w-6 h-6" />
                  <div className="text-center">
                    <span className="block text-[14px] font-bold">Indoor Dining</span>
                    <span className="block text-[11px] font-medium uppercase tracking-wider opacity-80">22% Admin Fee</span>
                  </div>
                </button>
                <button
                  onClick={() => setDiningLocation('outdoor')}
                  className={`flex-1 p-5 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                    diningLocation === 'outdoor'
                      ? 'bg-[#F6F1E8] border-[#6E7451] text-[#2F2B28]'
                      : 'bg-white border-[#D9CCBA] text-[#5A4638] hover:border-[#A67C52]/50'
                  }`}
                >
                  <SafeIcon icon={FiSun} className="w-6 h-6" />
                  <div className="text-center">
                    <span className="block text-[14px] font-bold">Outdoor Dining</span>
                    <span className="block text-[11px] font-medium uppercase tracking-wider opacity-80">25% Admin Fee</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Enhancements */}
            <div className="bg-[#F6F1E8]/50 p-7 rounded-2xl border border-[#D9CCBA]/40">
              <h4 className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5A4638] mb-5">Popular Enhancements</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { id: 'barware', label: 'Glass Barware ($3/pp)' },
                  { id: 'chargers', label: 'Charger Plates ($2/pp)' },
                  { id: 'beadChargers', label: 'Glass Bead Chargers ($7/pp)' },
                  { id: 'chairs', label: 'Chiavari Chairs ($10/pp)' },
                  { id: 'rings', label: 'Napkin Rings ($2/pp)' },
                  { id: 'lounge', label: 'Ballroom Lounge Setup ($800)' },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => toggleEnhancement(item.id)}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all text-left ${
                      enhancements[item.id] 
                        ? 'bg-[#6E7451] border-[#6E7451] text-white' 
                        : 'bg-white border-[#D9CCBA] text-[#2F2B28] hover:border-[#A67C52]'
                    }`}
                  >
                    <div className={`w-4.5 h-4.5 rounded border flex items-center justify-center ${enhancements[item.id] ? 'bg-white text-[#6E7451]' : 'border-[#D9CCBA]'}`}>
                      {enhancements[item.id] && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
                    </div>
                    <span className="text-[13px] font-semibold">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results Column */}
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <AnimatePresence mode="wait">
              {eventType === 'ceremonyOnly' ? (
                <motion.div key="ceremony" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
                  <Card className="border-[#D9CCBA] shadow-xl bg-white p-10 text-center">
                    <div className="w-16 h-16 bg-[#F6F1E8] rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={FiInfo} className="text-[#A67C52] w-8 h-8" />
                    </div>
                    <h3 className="font-serif text-2xl text-[#2F2B28] mb-4 font-semibold">Ceremony Only Preview</h3>
                    <p className="text-[#6E7451] font-bold text-3xl mb-6">$2,500 – $3,500</p>
                    <p className="text-[14px] text-[#5A4638] leading-[1.6] mb-8">
                      Ceremony-only events are typically offered 9:00 AM – 12:00 PM. Availability, setup, and final details should be confirmed with the team.
                    </p>
                    <Button href="https://paduaweddings.com/#contact" className="w-full bg-[#6E7451] py-4 text-white font-bold text-[12px]">Request a Proposal</Button>
                  </Card>
                </motion.div>
              ) : guestCount === 0 ? (
                <motion.div key="empty" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="border-[#D9CCBA] shadow-xl bg-white p-12 text-center flex flex-col items-center justify-center min-h-[400px]">
                    <div className="w-16 h-16 bg-[#F6F1E8] rounded-full flex items-center justify-center mb-6">
                      <SafeIcon icon={FiUsers} className="text-[#A67C52] w-7 h-7" />
                    </div>
                    <p className="text-[16px] text-[#2F2B28] font-medium leading-relaxed max-w-[240px]">
                      Enter your estimated guest count to preview a planning range.
                    </p>
                  </Card>
                </motion.div>
              ) : (
                <motion.div key="estimate" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="border-[#D9CCBA] shadow-2xl bg-white p-0 overflow-hidden">
                    <div className="bg-[#2F2B28] px-8 py-5 flex justify-between items-center">
                      <span className="text-[11px] uppercase tracking-[0.2em] font-black text-white">Planning Preview</span>
                      <span className="text-[12px] font-bold text-[#D9CCBA]">{guestCount} Guests</span>
                    </div>

                    <div className="p-8 space-y-5">
                      {/* Section 1: Eligible Service Items */}
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-[13px] font-bold text-[#2F2B28] uppercase tracking-wider">Eligible Service Subtotal</span>
                          <span className="text-[14px] font-bold text-[#2F2B28]">${totals?.eligibleServiceSubtotal.toLocaleString()}</span>
                        </div>
                        <div className="space-y-1.5 pl-3 border-l border-[#D9CCBA]">
                          <div className="flex justify-between text-[12px]">
                            <span className="text-[#5A4638]">Food & Dining Service</span>
                            <span className="font-medium text-[#2F2B28]">${totals?.foodCost.toLocaleString()}</span>
                          </div>
                          {totals?.ceremonyFee > 0 && (
                            <div className="flex justify-between text-[12px]">
                              <span className="text-[#5A4638]">Ceremony Setup Fee</span>
                              <span className="font-medium text-[#2F2B28]">${totals?.ceremonyFee.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-[12px]">
                            <span className="text-[#5A4638]">Enhancements Estimate</span>
                            <span className="font-medium text-[#2F2B28]">${totals?.enhancementCost.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Minimum Adjustment Alert */}
                      {totals?.isBelowMinimum && (
                        <div className="bg-[#F6F1E8] p-4 rounded-xl border border-[#D9CCBA]/50 space-y-1">
                          <div className="flex justify-between items-center text-[#A67C52]">
                            <span className="text-[11px] font-bold uppercase tracking-wide">Date Minimum Requirement</span>
                            <span className="text-[13px] font-bold">${totals?.minRequirement.toLocaleString()}</span>
                          </div>
                          <p className="text-[10px] text-[#7A6A5F] leading-tight italic">The Eligible Service Subtotal is below the date minimum. The minimum spend applies as the planning base for the administrative fee.</p>
                        </div>
                      )}

                      {/* Section 2: Mandatory Fees & Venue */}
                      <div className="space-y-3 pt-2 border-t border-[#D9CCBA]/40">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[13px] font-medium text-[#5A4638]">Estimated Administrative Fee ({Math.round(totals?.adminRate * 100)}%)</span>
                            <div className="group relative">
                              <SafeIcon icon={FiInfo} className="w-3 h-3 text-[#A67C52] cursor-help" />
                              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-48 p-2 bg-[#2F2B28] text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                This fee is not gratuity and is applied only to eligible catered/service subtotal items.
                              </div>
                            </div>
                          </div>
                          <span className="text-[14px] font-semibold text-[#2F2B28]">${Math.round(totals?.adminFee).toLocaleString()}</span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-[13px] font-medium text-[#5A4638]">Venue Rental</span>
                          <span className="text-[14px] font-semibold text-[#2F2B28]">${totals?.venueRental.toLocaleString()}</span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-[13px] font-medium text-[#5A4638]">Mandatory Parking Attendant</span>
                          <span className="text-[14px] font-semibold text-[#2F2B28]">${PARKING_FEE}</span>
                        </div>
                      </div>

                      {/* Section 3: Hosted Bar */}
                      <div className="space-y-2 pt-2 border-t border-[#D9CCBA]/40">
                        <div className="flex justify-between items-center">
                          <span className="text-[13px] font-medium text-[#5A4638]">Hosted Bar Estimate</span>
                          <span className="text-[14px] font-semibold text-[#2F2B28]">${totals?.barEstimate.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-[12px] font-medium text-[#7A6A5F]">Hosted Bar Sales Tax (9.75%)</span>
                          <span className="text-[13px] font-semibold text-[#5A4638]">${Math.round(totals?.barTax).toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Section 4: Final Total */}
                      <div className="pt-6 border-t-2 border-[#2F2B28]/10 bg-[#F6F1E8]/20 -mx-8 px-8 pb-4">
                        <div className="flex justify-between items-end">
                          <div className="space-y-1">
                            <span className="block text-[10px] font-black uppercase tracking-[0.15em] text-[#A67C52]">Estimated Planning Total</span>
                            <span className="block text-[13px] font-bold text-[#2F2B28]">Including Hosted Bar Tax</span>
                          </div>
                          <span className="text-[34px] font-bold text-[#6E7451] font-serif leading-none">
                            ${Math.round(totals?.totalIncludingAll).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-[10px] text-[#5A4638] text-right mt-4 font-medium italic leading-relaxed">
                          This preview is a planning tool. Final quotes include sales tax on all taxable items and final service adjustments.
                        </p>
                      </div>

                      {/* Policy Reminder */}
                      <div className={`mt-4 p-5 rounded-xl border transition-all duration-500 ${totals?.isBelowMinimum ? 'bg-[#FCFAF6] border-[#D9CCBA]' : 'bg-[#FCFAF6] border-[#6E7451]/30'}`}>
                        <div className="flex gap-3 items-start">
                          <SafeIcon icon={totals?.isBelowMinimum ? FiAlertCircle : FiCheck} className={`w-4 h-4 flex-shrink-0 mt-0.5 ${totals?.isBelowMinimum ? 'text-[#A67C52]' : 'text-[#6E7451]'}`} />
                          <p className="text-[11px] text-[#2F2B28] leading-[1.6]">
                            Event minimums exclude alcohol, venue rental, and parking. If your eligible service subtotal is below the date minimum, the minimum becomes the base for the administrative fee.
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom Disclaimer */}
        <div className="mt-16 text-center space-y-12">
          <p className="text-[13px] md:text-[14px] text-[#7A6A5F] max-w-3xl mx-auto italic leading-relaxed font-medium">
            This preview is designed to help couples explore possible planning ranges based on historical rates and standard packages. It is not a final quote, proposal, contract, or guarantee of availability. Final pricing is prepared by the Padua Weddings team based on date, guest count, menu selections, bar service, rentals, administrative fee, tax, and event details.
          </p>

          <div className="max-w-2xl mx-auto p-10 bg-white rounded-3xl border border-[#D9CCBA] shadow-soft">
            <h3 className="font-serif text-3xl text-[#2F2B28] mb-4 font-semibold">Ready for Your Custom Proposal?</h3>
            <p className="text-[#5A4638] font-medium mb-8 leading-relaxed">
              Share your guest count, preferred date, and menu direction with our team so we can prepare a custom estimate tailored to your vision.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button href="https://paduaweddings.com/#contact" className="px-10 bg-[#6E7451] text-white font-bold h-14">
                Request a Custom Proposal
              </Button>
              <Button href="https://tour.paduaweddings.com" variant="outline" className="px-10 border-[#D9CCBA] text-[#2F2B28] font-bold h-14">
                Schedule a Tour
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};